import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Button,
  Form,
  Alert,
  InputGroup,
  Tabs,
  Tab,
  Row,
  Col,
  Spinner
} from "react-bootstrap";
import {
  FiEye,
  FiEyeOff,
  FiMail,
  FiLock,
  FiUser,
  FiBriefcase,
  FiMapPin,
  FiSearch
} from "react-icons/fi";
import api, { BASE_URL } from "../../Utils/api";
import CNPJ from "../Generals/Inputs/CNPJ";
import { validarCNPJ } from "../../Utils/ValidarCNPJ";
import "./Login.css";

const IBGE_ESTADOS = "https://servicodados.ibge.gov.br/api/v1/localidades/estados";
const IBGE_MUNICIPIOS = (uf) =>
  `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios`;

function normalizarTextoBusca(valor) {
  return String(valor || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function encontrarCidade(listaCidades, nomeCidade, codIbge) {
  const cidadeNormalizada = normalizarTextoBusca(nomeCidade);
  const codIbgeNormalizado = codIbge ? String(codIbge) : "";

  return (
    listaCidades.find((cidade) => codIbgeNormalizado && String(cidade.cod_ibge || cidade.id) === codIbgeNormalizado) ||
    listaCidades.find((cidade) => normalizarTextoBusca(cidade.nome) === cidadeNormalizada) ||
    listaCidades.find((cidade) => cidadeNormalizada && normalizarTextoBusca(cidade.nome).includes(cidadeNormalizada))
  );
}

const CriarContaModal = ({ show, onHide, googleRegistration }) => {
  const [activeTab, setActiveTab] = useState("dados-pessoais");
  const [formValidated, setFormValidated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCepLoading, setIsCepLoading] = useState(false);
  const [isCnpjLoading, setIsCnpjLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    senha: "",
    confirmSenha: "",
    razao_social: "",
    fantasia: "",
    cnpj: "",
    cep: "",
    endereco: "",
    complemento: "",
    bairro: "",
    email_empresa: "",
    email_cobranca: "",
    email_receita_federal: "",
    uf: "",
    idCidade: ""
  });

  const [ufs, setUfs] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [cepError, setCepError] = useState("");
  const [cnpjError, setCnpjError] = useState("");

  const resetForm = useCallback(() => {
    setFormData({
      nome: "",
      email: "",
      senha: "",
      confirmSenha: "",
      razao_social: "",
      fantasia: "",
      cnpj: "",
      cep: "",
      endereco: "",
      complemento: "",
      bairro: "",
      email_empresa: "",
      email_cobranca: "",
      email_receita_federal: "",
      uf: "",
      idCidade: ""
    });
    setActiveTab("dados-pessoais");
    setFormValidated(false);
    setError("");
    setSuccess("");
    setCepError("");
    setCnpjError("");
    setCidades([]);
  }, [setFormValidated]);

  // Só busca UFs quando o modal for aberto (evita 401 na tela de login sem token)
  useEffect(() => {
    if (show) {
      fetchUfs();
    }
  }, [show]);

  useEffect(() => {
    if (!show) {
      resetForm();
    }
  }, [show, resetForm]);

  useEffect(() => {
    if (show && googleRegistration) {
      setFormData((prev) => ({
        ...prev,
        nome: googleRegistration.nome || "",
        email: googleRegistration.email || "",
        email_empresa: googleRegistration.email || "",
        senha: "",
        confirmSenha: ""
      }));
      setActiveTab("dados-empresa");
      setError("");
    }
  }, [show, googleRegistration]);

  const fetchUfs = async () => {
    try {
      const response = await api.get(`${BASE_URL}/api/enderecoDados/ufs`);
      setUfs(response.data);
    } catch (error) {
      try {
        const { data } = await api.get(IBGE_ESTADOS);
        setUfs((Array.isArray(data) ? data : []).map((e) => ({ sigla: e.sigla, nome: e.nome })));
      } catch (e2) {
        console.error("Erro ao buscar UFs:", e2);
        setUfs([
          { sigla: "SP", nome: "São Paulo" },
          { sigla: "RJ", nome: "Rio de Janeiro" },
          { sigla: "MG", nome: "Minas Gerais" }
        ]);
      }
    }
  };

  const fetchCidades = useCallback(async (uf) => {
    if (!uf) {
      setCidades([]);
      return [];
    }
    try {
      const response = await api.get(`${BASE_URL}/api/enderecoDados/cidades/${uf.toUpperCase()}`);
      setCidades(response.data);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      try {
        const { data } = await api.get(IBGE_MUNICIPIOS(uf.toUpperCase()));
        const lista = (Array.isArray(data) ? data : []).map((m) => ({
          id: String(m.id),
          nome: m.nome,
          cod_ibge: String(m.id)
        }));
        setCidades(lista);
        return lista;
      } catch (e2) {
        console.error(`Erro ao buscar cidades para UF ${uf}:`, e2);
        const fallback = [
          { id: "3550308", nome: "São Paulo", cod_ibge: "3550308" },
          { id: "3509502", nome: "Campinas", cod_ibge: "3509502" },
          { id: "3518800", nome: "Guarulhos", cod_ibge: "3518800" }
        ];
        setCidades(fallback);
        return fallback;
      }
    }
  }, []);

  const consultarCep = async () => {
    const cepNumbers = formData.cep.replace(/\D/g, "");

    if (cepNumbers.length !== 8) {
      setCepError("CEP deve conter 8 dígitos");
      return;
    }

    setIsCepLoading(true);
    setCepError("");

    try {
      const response = await api.get(`${BASE_URL}/api/enderecoDados/cep/${cepNumbers}`);
      const dadosCep = response.data || {};
      const uf = String(dadosCep.uf || "").toUpperCase();

      setFormData((prevData) => ({
        ...prevData,
        endereco: dadosCep.logradouro || "",
        bairro:   dadosCep.bairro     || "",
        uf,
        idCidade: "",
      }));

      if (uf && dadosCep.municipio) {
        const cidadesList = await fetchCidades(uf);
        const cidadeSimilar = encontrarCidade(cidadesList, dadosCep.municipio, dadosCep.ibge);
        if (cidadeSimilar) {
          setFormData((prev) => ({ ...prev, idCidade: cidadeSimilar.id }));
        }
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setCepError("CEP não encontrado");
      } else {
        setCepError("Erro ao consultar CEP. Verifique a conexão.");
      }
    } finally {
      setIsCepLoading(false);
    }
  };

  const limparCamposConsultaCnpj = useCallback(() => {
    setCidades([]);
    setFormData((prev) => ({
      ...prev,
      razao_social: "",
      fantasia: "",
      cep: "",
      endereco: "",
      complemento: "",
      bairro: "",
      email_receita_federal: "",
      uf: "",
      idCidade: ""
    }));
  }, []);

  const consultarCnpj = useCallback(async () => {
    const cnpjNumbers = String(formData.cnpj || "").replace(/\D/g, "");

    if (!cnpjNumbers) {
      setCnpjError("");
      return;
    }

    if (cnpjNumbers.length !== 14) {
      setCnpjError("CNPJ deve conter 14 dÃ­gitos");
      return;
    }

    if (!validarCNPJ(formData.cnpj)) {
      setCnpjError("CNPJ invÃ¡lido");
      return;
    }

    limparCamposConsultaCnpj();
    setIsCnpjLoading(true);
    setCnpjError("");

    try {
      const response = await api.get(`${BASE_URL}/api/auth/consultar-cnpj/${cnpjNumbers}`);
      const dados = response.data?.data || {};
      const uf = String(dados.uf || "").toUpperCase();
      const cidadesList = uf ? await fetchCidades(uf) : [];
      const cidadeEncontrada = encontrarCidade(cidadesList, dados.cidade);

      setFormData((prev) => ({
        ...prev,
        razao_social: dados.razao_social || "",
        fantasia: dados.fantasia || "",
        cep: dados.cep || "",
        endereco: dados.endereco || "",
        complemento: dados.complemento || "",
        bairro: dados.bairro || "",
        email_receita_federal: dados.email_receita_federal || "",
        uf,
        idCidade: cidadeEncontrada ? cidadeEncontrada.id : ""
      }));
    } catch (error) {
      console.error("Erro ao consultar CNPJ:", error);
      setCnpjError(error.response?.data?.message || "Nao foi possivel consultar o CNPJ informado.");
    } finally {
      setIsCnpjLoading(false);
    }
  }, [fetchCidades, formData.cnpj, limparCamposConsultaCnpj]);

  const handleChange = (e) => {
    const { id, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [id]: value
    }));

    if (id === "uf") {
      fetchCidades(value);
      setFormData((prevData) => ({ ...prevData, idCidade: "" }));
    }
  };

  const handleCNPJChange = (value) => {
    if (cnpjError) setCnpjError("");
    setFormData((prevData) => ({
      ...prevData,
      cnpj: value || ""
    }));
  };

  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, "").slice(0, 8);
    return numbers.length <= 5 ? numbers : `${numbers.slice(0, 5)}-${numbers.slice(5)}`;
  };

  const handleCEPChange = (e) => {
    const formattedValue = formatCEP(e.target.value);
    setFormData((prevData) => ({
      ...prevData,
      cep: formattedValue
    }));

    if (cepError) setCepError("");
  };

  const handleCepKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      consultarCep();
    }
  };

  const handleCepBlur = () => {
    const cepNumbers = (formData.cep || "").replace(/\D/g, "");
    if (cepNumbers.length === 8) consultarCep();
  };

  const validateDadosPessoais = () => {
    if (!formData.nome.trim()) return setError("Nome é obrigatório") || false;
    if (!formData.email.trim()) return setError("Email é obrigatório") || false;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      return setError("Por favor, insira um email válido") || false;
    if (!googleRegistration && formData.senha.length < 6)
      return setError("A senha deve ter pelo menos 6 caracteres") || false;
    if (!googleRegistration && formData.senha !== formData.confirmSenha)
      return setError("As senhas não coincidem") || false;

    return true;
  };

  const validateDadosEmpresa = () => {
    if (!formData.cnpj.trim()) return setError("CNPJ é obrigatório") || false;
    if (!validarCNPJ(formData.cnpj))
      return setError("CNPJ inválido") || false;
    if (!formData.razao_social.trim()) return setError("Razão Social é obrigatória") || false;
    if (!formData.fantasia.trim()) return setError("Nome Fantasia é obrigatório") || false;
    if (formData.cep.replace(/\D/g, "").length !== 8) return setError("CEP inválido") || false;
    if (!formData.endereco.trim()) return setError("Endereço é obrigatório") || false;
    if (!formData.bairro.trim()) return setError("Bairro é obrigatório") || false;
    if (!formData.uf) return setError("UF é obrigatória") || false;
    if (!formData.idCidade) return setError("Cidade é obrigatória") || false;

    if (
      formData.email_cobranca &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_cobranca)
    ) {
      return setError("Email de cobrança inválido") || false;
    }

    const emailEmpresa =
      formData.email_empresa.trim() || formData.email.trim();
    if (!emailEmpresa) {
      return setError("Email da entidade é obrigatório") || false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailEmpresa)) {
      return setError("Email da entidade inválido") || false;
    }

    return true;
  };

  const handleNextTab = () => {
    if (validateDadosPessoais()) {
      setError("");
      setFormData((prev) => ({
        ...prev,
        email_empresa: prev.email_empresa || prev.email,
      }));
      setActiveTab("dados-empresa");
    }
  };

  const handlePrevTab = () => {
    setError("");
    setActiveTab("dados-pessoais");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateDadosPessoais() || !validateDadosEmpresa()) return;

    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const emailEmpresa =
        formData.email_empresa.trim() || formData.email.trim();

      const empresa = {
        razao_social: formData.razao_social,
        nome_fantasia: formData.fantasia,
        cnpj: formData.cnpj,
        email: emailEmpresa,
        email_cobranca:
          formData.email_cobranca.trim() || emailEmpresa,
        ...(formData.email_receita_federal ? { email_receita_federal: formData.email_receita_federal } : {}),
        endereco: {
          cep: formData.cep.replace(/\D/g, ""),
          logradouro: formData.endereco,
          complemento: formData.complemento || "",
          bairro: formData.bairro,
          uf: formData.uf,
          cidade_id: formData.idCidade
        }
      };

      const dadosEnvio = googleRegistration
        ? {
            nome: formData.nome,
            google_registration_token: googleRegistration.token,
            empresa
          }
        : {
            nome: formData.nome,
            email: formData.email,
            senha: formData.senha,
            empresa
          };

      const endpoint = googleRegistration
        ? `${BASE_URL}/api/auth/register/google`
        : `${BASE_URL}/api/auth/register`;

      const response = await api.post(endpoint, dadosEnvio);

      if (response.status === 201) {
        setSuccess(
          response.data?.message ||
            "Cadastro concluído. Verifique seu e-mail para ativar a entidade."
        );
        setTimeout(() => {
          onHide();
          resetForm();
        }, 6000);
      }
    } catch (err) {
      console.error("Erro ao criar conta:", err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError("Erro ao criar conta. Por favor, tente novamente.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg"
      backdrop="static"
      dialogClassName="modal-dialog-top criar-conta-dialog"
      contentClassName="criar-conta-modal"
    >
      <Modal.Header closeButton className="criar-conta-header">
        <Modal.Title>{googleRegistration ? "Criar Conta com Google" : "Criar Conta"}</Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="criar-conta-body">
        {error && (
          <Alert variant="danger" onClose={() => setError("")} dismissible>
            <span style={{ whiteSpace: "pre-line" }}>{error}</span>
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" onClose={() => setSuccess("")} dismissible>
            {success}
          </Alert>
        )}
        
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => {
            if (k === "dados-empresa" && !validateDadosPessoais()) return;
            setError("");
            setActiveTab(k);
          }}
          id="criar-conta-tabs"
          className="criar-conta-tabs"
        >
          <Tab 
            eventKey="dados-pessoais" 
            title={
              <span>
                <FiUser className="me-2" />
                Dados Pessoais
              </span>
            }
          >
            <Form validated={formValidated}>
              {googleRegistration && (
                <Alert variant="info" className="mb-3">
                  Cadastro iniciado com Google. O e-mail abaixo ja foi confirmado pelo Google.
                </Alert>
              )}

              <Form.Group className="mb-3" controlId="nome">
                <Form.Label>Nome Completo*</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <FiUser />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Seu nome completo"
                    value={formData.nome}
                    onChange={handleChange}
                    required
                  />
                </InputGroup>
              </Form.Group>
              
              <Form.Group className="mb-3" controlId="email">
                <Form.Label>Email*</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <FiMail />
                  </InputGroup.Text>
                  <Form.Control
                    type="email"
                    placeholder="Seu email"
                    value={formData.email}
                    onChange={handleChange}
                    readOnly={!!googleRegistration}
                    required
                  />
                </InputGroup>
                <Form.Text className="text-muted">
                  Nunca compartilharemos seu email com terceiros.
                </Form.Text>
              </Form.Group>
              
              {!googleRegistration && (
                <>
              <Form.Group className="mb-3" controlId="senha">
                <Form.Label>Senha*</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <FiLock />
                  </InputGroup.Text>
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    placeholder="Senha"
                    value={formData.senha}
                    onChange={handleChange}
                    required
                  />
                  <Button 
                    variant="outline-secondary"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </Button>
                </InputGroup>
                <Form.Text className="text-muted">
                  Sua senha deve ter pelo menos 6 caracteres.
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-4" controlId="confirmSenha">
                <Form.Label>Confirmar Senha*</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <FiLock />
                  </InputGroup.Text>
                  <Form.Control
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirme sua senha"
                    value={formData.confirmSenha}
                    onChange={handleChange}
                    required
                  />
                  <Button 
                    variant="outline-secondary"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                  </Button>
                </InputGroup>
              </Form.Group>
                </>
              )}
              
              <div className="d-flex justify-content-end">
                <Button 
                  variant="primary" 
                  onClick={handleNextTab}
                >
                  Próximo
                </Button>
              </div>
            </Form>
          </Tab>
          
          <Tab 
            eventKey="dados-empresa" 
            title={
              <span>
                <FiBriefcase className="me-2" />
                Dados da Empresa
              </span>
            }
          >
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="cnpj">
                    <Form.Label>CNPJ*</Form.Label>
                    <div className="position-relative">
                      <CNPJ
                        id="cnpj"
                        value={formData.cnpj}
                        onChange={handleCNPJChange}
                        onBlur={consultarCnpj}
                        placeholder="00.000.000/0000-00"
                        className={`form-control ${cnpjError ? "is-invalid" : ""}`}
                      />
                      {isCnpjLoading && (
                        <Spinner
                          animation="border"
                          size="sm"
                          style={{ position: "absolute", right: 10, top: 10 }}
                        />
                      )}
                    </div>
                    {cnpjError && <div className="invalid-feedback d-block">{cnpjError}</div>}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="razao_social">
                    <Form.Label>Razão Social*</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Razão social da empresa"
                      value={formData.razao_social}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="fantasia">
                    <Form.Label>Nome Fantasia*</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Nome fantasia da empresa"
                      value={formData.fantasia}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="email_empresa">
                    <Form.Label>Email*</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FiMail />
                      </InputGroup.Text>
                      <Form.Control
                        type="email"
                        placeholder="Email de contato da entidade"
                        value={formData.email_empresa}
                        onChange={handleChange}
                        required
                      />
                    </InputGroup>
                    <Form.Text className="text-muted">
                      Por padrão, usamos o email informado nos dados pessoais.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="email_cobranca">
                    <Form.Label>Email para Cobrança</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Email para cobrança (opcional)"
                      value={formData.email_cobranca}
                      onChange={handleChange}
                    />
                    <Form.Text className="text-muted">
                      Se não informado, usaremos o email da entidade.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              <hr className="my-4" />
              <h5 className="mb-3">
                <FiMapPin className="me-2" />
                Endereço
              </h5>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="cep">
                    <Form.Label>CEP*</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        placeholder="00000-000"
                        value={formData.cep}
                        onChange={handleCEPChange}
                        onBlur={handleCepBlur}
                        onKeyPress={handleCepKeyPress}
                        isInvalid={!!cepError}
                        required
                      />
                      <Button 
                        variant="outline-secondary"
                        onClick={consultarCep}
                        disabled={isCepLoading}
                      >
                        {isCepLoading ? (
                          <Spinner 
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                          />
                        ) : (
                          <FiSearch />
                        )}
                      </Button>
                      <Form.Control.Feedback type="invalid">
                        {cepError}
                      </Form.Control.Feedback>
                    </InputGroup>
                    <Form.Text className="text-muted">
                      Digite o CEP (ex.: 88803-150) e clique na lupa ou saia do campo para preencher o endereço automaticamente.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="endereco">
                    <Form.Label>Logradouro*</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Rua, Avenida, etc."
                      value={formData.endereco}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="bairro">
                    <Form.Label>Bairro*</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Bairro"
                      value={formData.bairro}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="complemento">
                    <Form.Label>Complemento</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Complemento (opcional)"
                      value={formData.complemento}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="uf">
                    <Form.Label>Estado*</Form.Label>
                    <Form.Select
                      value={formData.uf}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Selecione o estado</option>
                      {ufs.map((uf) => (
                        <option key={uf.sigla} value={uf.sigla}>
                          {uf.nome}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="idCidade">
                    <Form.Label>Cidade*</Form.Label>
                    <Form.Select
                      value={formData.idCidade}
                      onChange={handleChange}
                      required
                      disabled={!formData.uf}
                    >
                      <option value="">Selecione a cidade</option>
                      {cidades.map((cidade) => (
                        <option key={cidade.id} value={cidade.id}>
                          {cidade.nome}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Tab>
        </Tabs>
      </Modal.Body>
      
      <Modal.Footer className="modal-footer-padrao criar-conta-footer">
        <div className="modal-footer-acoes-esquerda" />
        <div className="modal-footer-acoes-direita">
          {activeTab === "dados-pessoais" ? (
            <Button variant="light" className="modal-footer-btn-cancelar" onClick={onHide}>
              Cancelar
            </Button>
          ) : (
            <Button variant="light" className="modal-footer-btn-cancelar" onClick={handlePrevTab}>
              Voltar
            </Button>
          )}
          {activeTab === "dados-empresa" && (
            <Button
              variant="success"
              className="modal-footer-btn-salvar"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? "Criando conta..." : (googleRegistration ? "Criar Conta com Google" : "Criar Conta")}
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CriarContaModal;
